import React, { useState, useEffect, useContext } from "react";
import { useLocation, useHistory } from "react-router-dom";
import styles from "./ActivationForm.module.css";
import SimpleBack from "../../assets/simpleBackIcon.svg";
import { useBreadcrumb } from "../Breadcrumb/BreadcrumbContext";
import { useSidebar } from "../NewSideBar/SidebarContext";
import { AuthContext } from "../../contexts/auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";
import InfoPopup from "../InfoPopup";
import PageNotFound from "../PageNotFound";
import config from "../../config";
import { getCSSVariable } from "../../utils";
import LayoutContainer from "../LayoutContainer";
import SearchableDropdown from "../SearchableDropdown";

interface Field {
  key: string;
  label: string;
  mandatory?: boolean;
  type?: "text" | "select";
  options?: { value: string; label: string }[];
  hint?: string;
}

interface Channel {
  id: string;
  name: string;
  fields: Field[];
}

interface Connector {
  cloud: string;
  cloud_connection_metadata: object;
}

const GENERIC_ERROR_MESSAGE =
  "Check the highlighted fields and try again.";

const SAMPLE_VALUES: Record<string, Record<string, string>> = {
  ttd: {
    name: "The Trade Desk Brand Seat",
    advertiserId: "hh4jw69",
    advertiserSecretKey: "15gdev-xxxx-xxxx",
    dataProviderId: "saptharushi",
    dataProviderSecretKey: "e8746b0c-xxxx-xxxx",
    activationFlow: "Hashed Email/Phone",
    token: "ttd-api-token-xxxx-xxxx",
  },
  dv360: {
    name: "North America DV360 Seat",
    creds: "dv360-access@example.iam.gserviceaccount.com",
    advertiserId: "1234567890",
    seatId: "9876543210",
    appId: "com.example.android.app",
  },
  pubmatic: {
    name: "newPubMaticConnector",
    secretName: "sftp/pubmatic",
    dpid: "5808",
  },
  googleads: {
    name: "Brand Google Ads Account",
    customerId: "1234567890",
    credsType: "Service Account ID",
    credsSecret: "secret-creds-xxx-xxxx",
    loginCustomerId: "user@email.com",
    appId: "com.example.android.app",
    keySpace: "ANDROID",
  },
  aws: {
    name: "Audience Export S3 Bucket",
    destinationLocation: "prod/org/aws/audience-export",
    bucketName: "audience-export-bucket",
    bucketRegion: "us-east-1",
  },
  azure: {
    name: "Audience Export Container",
    secretNamePrefixDst: "prod/org/azure/audience-export",
    containerName: "audience-export",
  },
  mediasmart: {
    name: "Mediasmart Brand Seat",
    password: "your-mediasmart-password",
    mediasmartOrganizationId: "12345",
  },
  gcp: {
    name: "Audience Export Cloud Storage",
    destinationLocation: "prod/org/gcp/audience-export",
    bucketName: "audience-export-bucket",
    resultLocation: "exports/audiences",
  },
  meta: {
    name: "Meta Ads Brand Account",
    clientId: "1234567890123456",
    clientSecret: "secretkey-x",
    adAccountId: "act_1234567890",
    fbExchangeToken: "eaacede213543636",
  },
  sftp: {
    name: "Audience Delivery SFTP",
    destinationLocation: "prod/org/sftp/audience-delivery",
    bucketName: "sftp-transfer-bucket",
    bucketRegion: "us-east-1",
  },
};

const ActivationForm: React.FC = () => {
  const { postAdserverByOrgId, getOrganizations } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation<{
    selectedFields: Channel[];
    organizationData: object;
    existingConnectors: Connector[];
    selectedOrg: string;
    selectedOrgName: string;
    activeTab?: "adservers" | "cloud";
  }>();
  const { setBreadcrumbItems } = useBreadcrumb();
  const { mainMenuItems, setActiveSubItem, setIsSideBarMenuDisabled } =
    useSidebar();
  const { selectedFields } = location.state || { selectedFields: [] };
  const [selectedOrg, setSelectedOrg] = useState(location.state?.selectedOrg || "");
  const [selectedOrgName, setSelectedOrgName] = useState(location.state?.selectedOrgName || "");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const fedid =
    localStorage.getItem("federationkey") === "null"
      ? null
      : localStorage.getItem("federationkey");
  const [formData, setFormData] = useState<{ [key: string]: string }>(() =>
    selectedFields[0]?.id === "ttd" ? { activationFlow: "daid" } : {},
  );
  var sessionOrganizationId = localStorage.getItem("organizationkey");

  const [visibleSecrets, setVisibleSecrets] = useState<{
    [key: string]: boolean;
  }>({});
  const toggleVisibility = (key: string) => {
    setVisibleSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const secretFields = new Set([
    "clientSecret",
    "fbExchangeToken",
    "dataProviderSecretKey",
    "advertiserSecretKey",
    "password",
    "credsSecret",
    "token",
  ]);
  const isPlatformOrAppAdmin =
    localStorage.getItem("userRole") === config.superUserRole ||
    localStorage.getItem("userRole") === config.applicationAdminRole;

  const postAdserverByOrgIdFn = useAsyncFn({
    deferFn: postAdserverByOrgId,
  });

  const handleInputChange = (fieldKey: string, value: string) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [fieldKey]: value,
      };
      if (fieldKey === "credsType" && value !== "impersonated_service_account") {
        next.loginCustomerId = "";
      }
      if (fieldKey === "activationFlow" && value !== "hashed") {
        next.token = "";
      }
      return next;
    });
  };

  const isLoginCustomerIdVisible =
    formData.credsType === "impersonated_service_account";
  const isTtdHashedFlowVisible = formData.activationFlow === "hashed";

  const shouldShowField = (field: Field) => {
    if (field.key === "loginCustomerId") return isLoginCustomerIdVisible;
    if (field.key === "token") return isTtdHashedFlowVisible;
    return true;
  };

  const isFieldMandatory = (channelId: string, field: Field) => {
    if (channelId === "ttd") {
      if (
        isTtdHashedFlowVisible &&
        (field.key === "advertiserSecretKey" ||
          field.key === "dataProviderSecretKey")
      ) {
        return false;
      }
      // Token is only rendered for the hashed flow (see shouldShowField), so
      // it must not block submit while the daid flow is selected.
      if (field.key === "token") {
        return isTtdHashedFlowVisible;
      }
    }
    return Boolean(field.mandatory);
  };

  const areAllMandatoryFieldsFilled = selectedFields.every((channel) =>
    channel.fields.every((field) =>
      isFieldMandatory(channel.id, field) ? Boolean(formData[field.key]) : true,
    ),
  );

  const handleSubmit = async () => {
    setLoading(true);
    setSuccessMessage("");
    setFailureMessage("");
    const {
      bucketName,
      bucketRegion,
      destinationLocation,
      resultLocation,
      name,
      advertiserId,
      seatId,
      creds,
      customerId,
      credsType,
      credsSecret,
      loginCustomerId,
      keySpace,
      advertiserSecretKey,
      dataProviderId,
      dataProviderSecretKey,
      appId,
      clientId,
      clientSecret,
      adAccountId,
      fbExchangeToken,
      secretNamePrefixDst,
      containerName,
      password,
      mediasmartOrganizationId,
      token,
      secretName,
      dpid,
    } = formData || {};

    const isMeta = selectedFields[0]?.id === "meta";

    const adServerObj = {
      name,
      ...(selectedFields[0]?.id === "aws" || selectedFields[0]?.id === "sftp"
        ? { cloud: selectedFields[0]?.id, ad_server: "" }
        : { ad_server: selectedFields[0]?.id, cloud: "" }),
      ...(selectedFields[0]?.id === "dv360"
        ? {
            adserver_connection_metadata: {
              advertiser_id: advertiserId,
              seat_id: seatId,
              creds: creds,
              app_id: appId,
            },
          }
        : selectedFields[0]?.id === "pubmatic"
          ? {
              adserver_connection_metadata: {
                secret_name: secretName?.trim(),
                dpid: dpid?.trim(),
              },
            }
        : selectedFields[0]?.id === "googleads"
          ? {
              adserver_connection_metadata: {
                customer_id: customerId?.trim(),
                creds_type: credsType?.trim(),
                creds_secret: credsSecret?.trim(),
                ...(loginCustomerId?.trim() && {
                  login_customer_id: loginCustomerId.trim(),
                }),
                ...(appId?.trim() && { app_id: appId.trim() }),
                ...(keySpace?.trim() && { key_space: keySpace.trim() }),
              },
            }
        : isMeta
          ? {
              name,
              ad_server: "meta",
              cloud: "",
              adserver_connection_metadata: {
                client_id: clientId,
                client_secret: clientSecret,
                ad_account_id: adAccountId,
                fb_exchange_token: fbExchangeToken,
              },
            }
          : selectedFields[0]?.id === "ttd"
            ? {
                adserver_connection_metadata: {
                  advertiser_id: advertiserId,
                  advertiser_secret_key: advertiserSecretKey,
                  data_provider_id: dataProviderId,
                  data_provider_secret_key: dataProviderSecretKey,
                  ...(token?.trim() && { token: token.trim() }),
                },
              }
            : selectedFields[0]?.id === "azure"
              ? {
                  name,
                  ad_server: "",
                  cloud: "azure",
                  adserver_connection_metadata: null,
                  cloud_connection_metadata: {
                    secret_name_prefix_dst: secretNamePrefixDst,
                    container_name: containerName,
                  },
                  common: false,
                }
              : selectedFields[0]?.id === "gcp"
                ? {
                    name,
                    ad_server: "",
                    cloud: "gcp",
                    adserver_connection_metadata: null,
                    cloud_connection_metadata: {
                      secret_name_prefix_src: "",
                      secret_name_prefix_dst: destinationLocation,
                      bucket_name: bucketName,
                      bucket_region: "",
                      result_location: resultLocation,
                    },
                    common: false,
                  }
                : selectedFields[0]?.id === "mediasmart"
                  ? {
                      name,
                      ad_server: "mediasmart",
                      cloud: "",
                      cloud_connection_metadata: null,
                      adserver_connection_metadata: {
                        user_name: name,
                        password: password,
                        org_id: mediasmartOrganizationId,
                      },
                      common: false,
                    }
                  : {
                      cloud_connection_metadata:
                        selectedFields[0]?.id === "aws" ||
                        selectedFields[0]?.id === "sftp"
                          ? {
                              secret_name_prefix_src: "",
                              secret_name_prefix_dst: destinationLocation,
                              bucket_name: bucketName,
                              bucket_region: bucketRegion,
                            }
                          : {},
                    }),
    };

    try {
      const response = await postAdserverByOrgIdFn.run({
        organizationId: isPlatformOrAppAdmin ? selectedOrg : sessionOrganizationId,
        adServer: adServerObj,
      });
      if (response) {
        setLoading(false);
        setSuccessMessage("Update successful! ");
      } else {
        setLoading(false);
        setFailureMessage(GENERIC_ERROR_MESSAGE);
        throw new Error(GENERIC_ERROR_MESSAGE);
      }
    } catch (error) {
      setFailureMessage(error.message || GENERIC_ERROR_MESSAGE);
      setLoading(false);
    }
  };

  const returnToActivationChannels = () => {
    const { activeTab } = location.state || {};
    history.push(
      "/activation-channels",
      activeTab ? { activeTab } : undefined,
    );
  };

  const handleCancel = () => {
    const isFormDataEmpty = Object.values(formData).every(
      (value) => value.trim() === "",
    );

    if (isFormDataEmpty) {
      returnToActivationChannels();
    } else setFormData({});
  };

  useEffect(() => {
    setBreadcrumbItems([
      { label: "Discover", path: "/entry-hub?console=Discover" },
      { label: "Curation", path: "/federation-selection" },
      { label: "Activation Channels", path: "/activation-channels" },
      { label: "Activation Form", path: "/activation-form" },
    ]);
    const sideBarMenuToHighlight = mainMenuItems
      .flatMap((item) => item.subMenu)
      .find((subItem) =>
        subItem.urls.some((url) => url.includes("/activation-channels")),
      )?.label;
    setActiveSubItem(sideBarMenuToHighlight);

    setIsSideBarMenuDisabled({
      fed: false,
      cp: fedid === null ? true : false,
      cr: fedid === null ? true : false,
      pr: fedid === null ? true : false,
      ca: false,
      ah: fedid === null ? true : false,
      dm: fedid === null ? true : false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const LoadingSpinner = () => (
    <div className={styles.spinnerOverlay}>
      <div className={styles.spinner}></div>
    </div>
  );
  const storedPermissions = JSON.parse(
    localStorage.getItem("userPermissions") || "{}",
  );

  const fieldId = selectedFields[0]?.id;
  const selectedData = SAMPLE_VALUES[fieldId] || {};

  return storedPermissions?.activationconnectors === "no" ? (
    <PageNotFound />
  ) : (
    <LayoutContainer>
      <div className={styles.activationformContainer}>
        {loading && <LoadingSpinner />}
        {successMessage && (
          <InfoPopup
            isOpen={Boolean(successMessage)}
            onClose={() => {
              setSuccessMessage(null);
              returnToActivationChannels();
            }}
            headerText="Information"
            displayText="Channel details updated succesfully!"
            freeText
          />
        )}
        {failureMessage && (
          <InfoPopup
            isOpen={Boolean(failureMessage)}
            onClose={() => {
              setFailureMessage(null);
              returnToActivationChannels();
            }}
            headerText="Information!"
            displayText={failureMessage}
            freeText
          />
        )}
        <div className={styles.headerSection}>
          <button
            className={styles.backButton}
            onClick={() => history.push("/activation-channels")}
          >
            <img src={SimpleBack} alt="back" />
            Back
          </button>
          <h1 className={styles.header}>Activation Channels</h1>
        </div>
        <div
          className={styles.container}
          style={
            {
              "--active-bg-color": getCSSVariable("--active-bg-color"),
              "--Primary-Color-Shade-1": getCSSVariable(
                "--Primary-Color-Shade-1",
              ),
            } as React.CSSProperties
          }
        >
          <div className={styles.contentRow}>
            <div className={styles.formSection}>
              {selectedFields.map((channel) => (
                <div key={channel.id}>
                  <h3 className={styles.channelHeader}>{channel?.name}</h3>
                  {isPlatformOrAppAdmin && (
                    <div className={styles.orgSelectorRow}>
                      <SearchableDropdown<{ id: string; name: string }>
                        fetchOptions={(name) =>
                          getOrganizations({ args: { name, page_size: 8 } })
                        }
                        onSelect={(org) => {
                          setSelectedOrg(org.id);
                          setSelectedOrgName(org.name);
                        }}
                        onClear={() => {
                          setSelectedOrg("");
                          setSelectedOrgName("");
                        }}
                        getLabel={(org) => org.name}
                        getValue={(org) => org.id}
                        selectedValue={selectedOrg}
                        selectedLabel={selectedOrgName}
                        placeholder="Search organization…"
                        className={styles.orgSelect}
                      />
                    </div>
                  )}
                  {channel.fields.filter(shouldShowField).map((field) => (
                    <div className={styles.formGroup} key={field.key}>
                      <label className={styles.label} htmlFor={field.key}>
                        <strong className={styles.fieldContainer}>
                          {field.label}
                          {isFieldMandatory(channel.id, field) && (
                            <span className={styles.mandatory}>*</span>
                          )}
                        </strong>
                      </label>
                      {field.hint && (
                        <p className={styles.fieldHint}>{field.hint}</p>
                      )}
                      <div className={styles.inputContainer}>
                        {field.type === "select" ? (
                          <select
                            id={field.key}
                            className={styles.inputField}
                            value={formData[field.key] || ""}
                            onChange={(e) =>
                              handleInputChange(field.key, e.target.value)
                            }
                          >
                            <option value="">
                              {field.mandatory
                                ? `Select ${field.label}`
                                : "Select (optional)"}
                            </option>
                            {field.options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <>
                            <input
                              id={field.key}
                              type={
                                secretFields.has(field.key) &&
                                !visibleSecrets[field.key]
                                  ? "password"
                                  : "text"
                              }
                              className={styles.inputField}
                              placeholder={field.label}
                              value={formData[field.key] || ""}
                              onChange={(e) =>
                                handleInputChange(field.key, e.target.value)
                              }
                            />
                            {secretFields.has(field.key) && (
                              <button
                                type="button"
                                onClick={() => toggleVisibility(field.key)}
                                className={styles.hideButton}
                              >
                                {visibleSecrets[field.key] ? "Hide" : "View"}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className={styles.infoPane}>
              <p className={styles.infoPaneTitle}>Sample Values</p>
              {selectedFields[0]?.fields.filter(shouldShowField).map((field) => {
                const sampleValue = selectedData[field.key];
                if (!sampleValue) return null;
                return (
                  <div className={styles.sampleItems} key={field.key}>
                    <span className={styles.sampleLabel}>{field.label}</span>
                    <span className={styles.sampleVal}>{sampleValue}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <button className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
            <button
              className={styles.previousButton}
              onClick={returnToActivationChannels}
            >
              Previous
            </button>
            <div className={styles.tooltipContainer}>
              <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={
                  isPlatformOrAppAdmin
                    ? !areAllMandatoryFieldsFilled || !selectedOrg
                    : !areAllMandatoryFieldsFilled
                }
              >
                Submit
              </button>
              {!areAllMandatoryFieldsFilled && (
                <span className={styles.tooltip}>
                  Please fill in all mandatory fields
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </LayoutContainer>
  );
};

export default ActivationForm;
